import type { FunctionComponent } from 'react';
import React from 'react';
import { locale } from '@trello/config';
import {
  N0,
  N400,
  N500,
  Green500,
  Orange500,
  Purple500,
  Sky500,
  Lime500,
  Pink500,
  Red500,
  Yellow500,
  TrelloBlue500,
} from '@trello/colors';
import { token } from '@trello/theme';

import { Badge, IconBadge } from './Badge';
import { SelectionModeIcon } from '../icons/SelectionModeIcon';

const formatDate = (d: Date) =>
  d.toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
  });

/**
 * CheckboxFieldBadge
 */
interface CheckboxFieldBadgeProps {
  className?: string;
  field: { name: string };
  item?: {
    value?: {
      checked: string;
    };
  };
}

export const CheckboxFieldBadge: FunctionComponent<CheckboxFieldBadgeProps> = ({
  className,
  field,
  item,
}) => {
  if (!item || !item.value || item.value.checked !== 'true') {
    return null;
  }
  return (
    <IconBadge className={className} icon={<SelectionModeIcon />}>
      {field.name}
    </IconBadge>
  );
};

/**
 * DateField Badge Badge
 */
interface DateFieldBadgeProps {
  className?: string;
  field: { name: string };
  item?: {
    value?: {
      date: string;
    };
  };
}

export const DateFieldBadge: FunctionComponent<DateFieldBadgeProps> = ({
  className,
  field,
  item,
}) => {
  if (!item || !item.value || !item.value.date) {
    return null;
  }
  const formatted = formatDate(new Date(item.value.date));

  return (
    <Badge className={className}>
      <span>{`${field.name}: ${formatted}`}</span>
    </Badge>
  );
};

type ListFieldOptionColors =
  | 'green'
  | 'yellow'
  | 'orange'
  | 'red'
  | 'blue'
  | 'purple'
  | 'sky'
  | 'lime'
  | 'pink'
  | 'black'
  | 'none';

interface ListFieldBadgeProps {
  className?: string;
  field?: {
    name: string;
    options: {
      id: string;
      color: ListFieldOptionColors;
      value?: {
        text: string;
      };
    }[];
  };
  item?: {
    idValue: string;
  };
}

export const ListFieldBadge: FunctionComponent<ListFieldBadgeProps> = ({
  className,
  field,
  item,
}) => {
  if (!item || !item.idValue || !field || !field.options) {
    return null;
  }

  const option = field.options.find((opt) => opt.id === item.idValue);
  if (!option || !option.value || !option.value.text) {
    return null;
  }

  let bgColor = 'transparent';
  let fontColor;
  const ListFieldColorMap: Map<string, string> = new Map([
    ['green', token('color.background.accent.green.subtle', Green500)],
    ['yellow', token('color.background.accent.yellow.subtle', Yellow500)],
    ['orange', token('color.background.accent.orange.subtle', Orange500)],
    ['red', token('color.background.accent.red.subtle', Red500)],
    ['blue', token('color.background.accent.blue.subtle', TrelloBlue500)],
    ['purple', token('color.background.accent.purple.subtle', Purple500)],
    ['sky', token('color.background.accent.teal.subtle', Sky500)],
    ['lime', token('trello.color.background.accent.lime.subtle', Lime500)],
    ['pink', token('color.background.accent.magenta.subtle', Pink500)],
    ['black', token('color.background.accent.gray.subtle', N500)],
  ]);

  if (
    option.color &&
    option.color !== 'none' &&
    ListFieldColorMap.has(option.color)
  ) {
    bgColor = ListFieldColorMap.get(option.color)!;
    fontColor = token('color.text', N0);
  } else {
    fontColor = token('color.text', N400);
  }

  return (
    <Badge className={className} bgColor={bgColor} fontColor={fontColor}>
      <span>{`${field.name}: ${option.value.text}`}</span>
    </Badge>
  );
};

/**
 * NumberField Badge
 */
interface NumberFieldBadgeProps {
  className?: string;
  field: {
    name: string;
  };
  item?: {
    value?: {
      number: string;
    };
  };
}

export const NumberFieldBadge: FunctionComponent<NumberFieldBadgeProps> = ({
  className,
  field,
  item,
}) => {
  if (!item || !item.value || !item.value.number) {
    return null;
  }
  const formatNumber = (num: number) =>
    num.toLocaleString(window.navigator.language, {
      maximumFractionDigits: 10,
    });

  const formatted = formatNumber(parseFloat(item.value.number));

  return (
    <Badge className={className}>
      <span>{`${field.name}: ${formatted}`}</span>
    </Badge>
  );
};

/**
 * TextField Badge
 */
interface TextFieldBadgeProps {
  className?: string;
  field: {
    name: string;
  };
  item?: {
    value?: {
      text: string;
    };
  };
}

export const TextFieldBadge: FunctionComponent<TextFieldBadgeProps> = ({
  className,
  field,
  item,
}) => {
  if (!item || !item.value || !item.value.text) {
    return null;
  }

  return (
    <Badge className={className}>
      <span>{`${field.name}: ${item.value.text}`}</span>
    </Badge>
  );
};
